// Taxonomy rows carry an explicit `tier1`/`tier2`/`tier3` hierarchy alongside `parentId`. The tier
// fields are what labels and the music check are built from, because the `parentId` chains dangle:
// the top-level "Music" row points at a `JLBCU7` parent and "Public Radio" at a `SPSHQ5` parent,
// neither of which the endpoint ever returns. Walking `parentId` therefore drops ancestors that the
// tier fields spell out correctly.
const TIER_KEYS = ["tier1", "tier2", "tier3", "tier4"]

// The Zeno "Music" plan gating used to key off the legacy `station_category` id. It now keys off the
// "Music" tier, which covers both the top-level Music category itself (its tier2 is "Music") and
// every genre nested under it (their tier1 is "Music").
const MUSIC_TIER = "Music"

const genreTiers = (genre) => {
  if (!genre) {
    return []
  }

  const tiers = TIER_KEYS.map((key) => genre[key]).filter(Boolean)

  return tiers.length ? tiers : [genre.name].filter(Boolean)
}

export const iabGenres = (state) => {
  return state.genres
}

export const isLoadingIABGenres = (state) => {
  return state.isLoading
}

export const iabGenresById = (state) => {
  return new Map(state.genres.map((genre) => [genre.id, genre]))
}

export const iabGenreNames = (state) => {
  return new Set(state.genres.map((genre) => genre.name))
}

/**
 * Human readable path for a genre, e.g. "Music › Rock Music › Classic Rock".
 *
 * Leading tiers that are grouping buckets rather than selectable categories ("Entertainment",
 * "Genres") are trimmed, so "Genres › Talk Radio › Public Radio" reads "Talk Radio › Public Radio".
 */
export const iabGenrePath = (state, getters) => (genreId) => {
  const tiers = genreTiers(getters.iabGenresById.get(genreId))

  while (tiers.length > 1 && !getters.iabGenreNames.has(tiers[0])) {
    tiers.shift()
  }

  return tiers.join(" › ")
}

export const iabGenreOptions = (state, getters) => {
  return state.genres
    .map((genre) => ({
      value: genre.id,
      caption: getters.iabGenrePath(genre.id),
    }))
    .sort((a, b) => a.caption.localeCompare(b.caption))
}

export const isMusicIABGenre = (state, getters) => (genreId) => {
  return genreTiers(getters.iabGenresById.get(genreId)).includes(MUSIC_TIER)
}

export const hasMusicIABGenre = (state, getters) => (genreIds) => {
  return (genreIds || []).some((genreId) => getters.isMusicIABGenre(genreId))
}
